/**
 * Find a SKU and see everything about it.
 *
 * Built because "is this code in SQL Accounting, and what is it?" was a question that took a
 * developer and a database session to answer. Three things it has to handle, all of which come
 * from LSK's real data rather than from a wishlist:
 *
 *   The code on screen is not always the code stored. Five items carry a trailing newline or
 *   spaces (discovery D31), and an exact lookup against SQL Accounting with the visible code
 *   returns "No record found." — which reads as "no such item" rather than "your code has a
 *   newline in it". The panel says so in as many words.
 *
 *   Warehouse staff read codes off TAGS, and a tag carries a colour child or a set piece that SQL
 *   Accounting has never heard of (D23). Searching one resolves to the parent and names the child
 *   that matched, so the answer is "yes, that is this item" rather than "no results".
 *
 *   A parent has 47-164 colour children. Listing them flat would bury the item's own attributes
 *   under a wall of colours it has never stocked, so they sit behind a count, scanned ones first.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, qty } from '../lib/api';
import { businessDate } from '../lib/time';
import { snapshotAge } from '../lib/snapshotAge';
import { Card, Table, Loading, Empty, cell } from '../components/ui';

type Hygiene = { dirty: boolean; stored: string; issue: string | null };

type MatchedVia = {
  childSku: string;
  parentSku: string;
  kind: 'colour' | 'set-piece';
  colourName: string | null;
  piece: string | null;
};

type SearchRow = {
  itemcode: string;
  description: string | null;
  description2: string | null;
  stockgroup: string | null;
  uom: string | null;
  isActive: boolean;
  rank: number;
  hygiene: Hygiene;
  matchedVia: MatchedVia | null;
  /** Total across every location at the latest snapshot. Null when no snapshot covers it. */
  onHand: string | null;
  onHandLocations: number;
};

type Colour = {
  childSku: string;
  colourCode: string | null;
  colourName: string | null;
  supplier: string | null;
};

type Detail = {
  itemcode: string;
  description: string | null;
  description2: string | null;
  stockgroup: string | null;
  uom: string | null;
  isActive: boolean;
  hygiene: Hygiene;
  attributes: Record<string, unknown> | null;
  snapshotDate: string | null;
  balances: Array<{
    location: string;
    on_hand: string;
    available: string;
    committed_so: string;
    on_order_po: string;
  }>;
  colours: { seen: Colour[]; unseen: Colour[]; total: number; capped: boolean };
  setPieces: Array<{ childSku: string; piece: string | null; qtyPerSet: string | null }>;
};

/** A stored code that differs from the visible one is the reason an exact lookup will fail. */
function HygieneWarning({ hygiene }: { hygiene: Hygiene }) {
  if (!hygiene.dirty) return null;
  return (
    <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      ⚠ <strong>This code is {hygiene.issue}.</strong> SQL Accounting holds it as{' '}
      <code className="rounded bg-white px-1 font-mono">&quot;{hygiene.stored}&quot;</code>, so an
      exact lookup using the code shown above returns “No record found.” Anything keying on this
      item — a posted line, a pushed plan line — has to carry the stored form.
    </p>
  );
}

/** Why this row surfaced, when it was not the code itself that matched. */
function MatchedVia({ via }: { via: MatchedVia }) {
  return (
    <span
      className="text-xs text-brand-600"
      title={`${via.childSku} is a ${via.kind === 'colour' ? 'colour variant' : 'set piece'} that rolls up to this item`}
    >
      matched tag SKU <span className="font-mono">{via.childSku}</span>
      {via.colourName ? ` · ${via.colourName}` : via.piece ? ` · piece ${via.piece}` : ''}
    </span>
  );
}

export default function InventorySearch() {
  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  // Debounced: every keystroke against a 38k-row LIKE scan is a query nobody asked for.
  useEffect(() => {
    const t = setTimeout(() => setTerm(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  const search = useQuery({
    queryKey: ['inv-search', term],
    enabled: term.length >= 2,
    queryFn: () =>
      api.get<{
        query: string;
        results: SearchRow[];
        total: number;
        truncated: number;
        snapshotDate: string | null;
      }>(`/api/v1/bi/inventory/search?q=${encodeURIComponent(term)}`),
  });

  const detail = useQuery({
    queryKey: ['inv-search-detail', selected],
    enabled: selected !== null,
    queryFn: () =>
      api.get<Detail>(`/api/v1/bi/inventory/search/${encodeURIComponent(selected ?? '')}`),
  });

  const rows = search.data?.results ?? [];

  return (
    <div className="space-y-6">
      <Card
        title="Find a SKU"
        subtitle="Item code, description, or the SKU printed on a tag. A tag SKU resolves to the SQL Accounting item it rolls up to."
      >
        <input
          type="search"
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="EM-3FD-3, 3FD, BEDFRAME, or a code read off a tag…"
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
        />

        {term.length > 0 && term.length < 2 ? (
          <Empty>Type at least two characters.</Empty>
        ) : term.length < 2 ? (
          <Empty>Start typing to search {`38,090`} stock items.</Empty>
        ) : search.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            Nothing matches “{term}”. It is not an item code, a description, or a tag SKU we hold a
            mapping for.
          </Empty>
        ) : (
          <div className="mt-4">
            {(search.data?.truncated ?? 0) > 0 && (
              <p className="mb-2 text-xs text-slate-500">
                Showing {rows.length} of {search.data?.total} matches — narrow the search to see the
                rest.
              </p>
            )}
            {/* The date the On hand column describes. Without it a two-day-old figure reads as
                "now", which is exactly how a correct number got reported as a discrepancy. */}
            {(() => {
              const age = snapshotAge(search.data?.snapshotDate ?? null);
              if (age === null) return null;
              return (
                <p
                  className={`mb-2 text-xs ${age.stale ? 'font-medium text-amber-700' : 'text-slate-500'}`}
                >
                  On hand is SQL Accounting&rsquo;s figure as at{' '}
                  {businessDate(search.data?.snapshotDate ?? '')} ({age.label})
                  {age.stale && ' — stock may have moved since'}
                </p>
              );
            })()}
            <Table
              head={[
                'Code',
                'Description',
                'On hand',
                'Group',
                'UOM',
                'Status',
                'Why it matched',
                '',
              ]}
              rows={rows.map((r) => [
                <span className="font-mono text-xs">
                  {r.itemcode}
                  {r.hygiene.dirty && (
                    <span className="ml-1 text-amber-600" title={`Code ${r.hygiene.issue}`}>
                      ⚠
                    </span>
                  )}
                </span>,
                <span>
                  {r.description ?? <span className="text-slate-400">—</span>}
                  {r.description2 && (
                    <span className="ml-1 text-xs text-slate-400">{r.description2}</span>
                  )}
                </span>,
                // The question people actually arrive with. Without it the list only answers
                // "does this code exist?", and the count columns beside it get read as quantities.
                r.onHand === null
                  ? cell(
                      -1,
                      <span className="text-slate-400" title="no stock snapshot covers this item">
                        —
                      </span>,
                    )
                  : cell(
                      Number(r.onHand),
                      <span>
                        <span className="font-medium">{qty(r.onHand)}</span>
                        {r.onHandLocations > 1 && (
                          <span className="ml-1 text-xs text-slate-400">
                            in {r.onHandLocations} locations
                          </span>
                        )}
                      </span>,
                    ),
                r.stockgroup ?? '—',
                r.uom ?? '—',
                r.isActive ? (
                  <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                    active
                  </span>
                ) : (
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    inactive
                  </span>
                ),
                // Sort by rank, not by the prose — "matched tag SKU" must not sort alphabetically.
                cell(
                  r.rank,
                  r.matchedVia ? (
                    <MatchedVia via={r.matchedVia} />
                  ) : (
                    <span className="text-xs text-slate-400">code or description</span>
                  ),
                ),
                <button
                  onClick={() => setSelected(selected === r.itemcode ? null : r.itemcode)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                >
                  {selected === r.itemcode ? 'Hide' : 'Details'}
                </button>,
              ])}
            />
          </div>
        )}
      </Card>

      {selected && (
        <ItemDetail
          itemcode={selected}
          detail={detail.data}
          isLoading={detail.isLoading}
          isError={detail.isError}
        />
      )}
    </div>
  );
}

function ItemDetail({
  itemcode,
  detail,
  isLoading,
  isError,
}: {
  itemcode: string;
  detail: Detail | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const [showColours, setShowColours] = useState(false);
  const [colourFilter, setColourFilter] = useState('');

  const colours = useMemo(() => {
    if (!detail) return [];
    // Scanned children first — for a parent with 164 colours, the handful that have actually been
    // through a warehouse are the only ones anyone is looking for.
    const all = [...detail.colours.seen, ...detail.colours.unseen];
    const f = colourFilter.trim().toUpperCase();
    if (!f) return all;
    return all.filter(
      (c) =>
        c.childSku.toUpperCase().includes(f) ||
        (c.colourName ?? '').toUpperCase().includes(f) ||
        (c.colourCode ?? '').toUpperCase().includes(f),
    );
  }, [detail, colourFilter]);

  if (isLoading) {
    return (
      <Card title={itemcode}>
        <Loading />
      </Card>
    );
  }
  if (isError || !detail) {
    return (
      <Card title={itemcode}>
        <Empty>No such item in the stock item master.</Empty>
      </Card>
    );
  }

  const seenCount = detail.colours.seen.length;
  const totalOnHand = detail.balances.reduce((sum, b) => sum + Number(b.on_hand), 0);

  return (
    <div className="space-y-6">
      <Card
        title={detail.itemcode}
        subtitle={
          [detail.description, detail.description2].filter(Boolean).join(' · ') || undefined
        }
      >
        <div className="space-y-4">
          <HygieneWarning hygiene={detail.hygiene} />

          <div className="grid gap-3 sm:grid-cols-4">
            <Fact label="Stock group" value={detail.stockgroup ?? '—'} />
            <Fact label="UOM" value={detail.uom ?? String(detail.attributes?.['suom'] ?? '—')} />
            <Fact label="Status" value={detail.isActive ? 'active' : 'inactive'} />
            <Fact
              label="On hand"
              value={qty(String(totalOnHand))}
              sub={
                detail.snapshotDate
                  ? `across ${detail.balances.length} location(s), as at ${businessDate(detail.snapshotDate)}`
                  : 'no balance snapshot held'
              }
            />
          </div>
        </div>
      </Card>

      <Card
        title="Stock by location"
        subtitle={
          detail.snapshotDate
            ? `SQL Accounting balance as at ${businessDate(detail.snapshotDate)}. Only locations you have access to are shown.`
            : 'No balance snapshot held yet.'
        }
      >
        {detail.balances.length === 0 ? (
          <Empty>
            No stock at any location you can see. That is not the same as none anywhere — location
            access is restricted per user.
          </Empty>
        ) : (
          <Table
            head={['Location', 'On hand', 'Available', 'Committed (SO)', 'On order (PO)']}
            rows={detail.balances.map((b) => [
              b.location,
              cell(Number(b.on_hand), <span className="font-medium">{qty(b.on_hand)}</span>),
              cell(Number(b.available), qty(b.available)),
              cell(Number(b.committed_so), qty(b.committed_so)),
              cell(Number(b.on_order_po), qty(b.on_order_po)),
            ])}
          />
        )}
      </Card>

      {/* Set pieces are always exactly two and are the item's real physical decomposition, so they
          are never hidden. Colours are 47-164 and are. */}
      {detail.setPieces.length > 0 && (
        <Card
          title="Set pieces"
          subtitle="This item ships as separate physical pieces, each with its own tag. A set is only complete when both are present."
        >
          <Table
            head={['Piece', 'Tag SKU', 'Qty per set']}
            rows={detail.setPieces.map((p) => [
              p.piece ?? '—',
              <span className="font-mono text-xs">{p.childSku}</span>,
              p.qtyPerSet ?? '—',
            ])}
          />
        </Card>
      )}

      <Card
        title="Colour variants"
        subtitle="The SKUs the tags actually carry. SQL Accounting knows only the parent above — this is the translation."
        actions={
          detail.colours.total > 0 && (
            <button
              onClick={() => setShowColours((v) => !v)}
              className="rounded border border-slate-300 px-2.5 py-1 text-xs hover:bg-brand-50"
            >
              {showColours ? 'Hide' : `Show all ${detail.colours.total}`}
            </button>
          )
        }
      >
        {detail.colours.total === 0 ? (
          <Empty>No colour variants mapped. This item is scanned under its own code.</Empty>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              <strong>{detail.colours.total}</strong> colour variant
              {detail.colours.total === 1 ? '' : 's'} mapped ·{' '}
              {seenCount > 0 ? (
                <span className="text-emerald-700">{seenCount} seen in a scan</span>
              ) : (
                <span className="text-slate-500">none seen in a scan yet</span>
              )}
              {detail.colours.capped && (
                <span className="text-amber-700"> · showing the first 500 only</span>
              )}
            </p>
            {seenCount === 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Every variant below is a mapping, not an observation — no LSK stock is tagged yet.
                As tagging rolls out, the ones actually scanned move to the top.
              </p>
            )}

            {showColours && (
              <div className="mt-4">
                <input
                  type="search"
                  value={colourFilter}
                  onChange={(e) => setColourFilter(e.target.value)}
                  placeholder="Filter by colour code, name or SKU…"
                  className="mb-3 w-full rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
                />
                {colours.length === 0 ? (
                  <Empty>No variant matches “{colourFilter}”.</Empty>
                ) : (
                  <Table
                    head={['Tag SKU', 'Colour code', 'Colour name', 'Supplier', 'Seen']}
                    rows={colours.map((c) => [
                      <span className="font-mono text-xs">{c.childSku}</span>,
                      c.colourCode ?? '—',
                      c.colourName ?? '—',
                      c.supplier ?? '—',
                      detail.colours.seen.some((s) => s.childSku === c.childSku) ? (
                        <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                          scanned
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      ),
                    ])}
                  />
                )}
              </div>
            )}
          </>
        )}
      </Card>

      <Card
        title="All master attributes"
        subtitle="Every field SQL Accounting returns for this item, unfiltered. The schema is not fixed, so nothing here is a curated list."
      >
        <Table
          head={['Field', 'Value']}
          rows={Object.entries(detail.attributes ?? {}).map(([k, v]) => [
            <span className="font-mono text-xs text-slate-500">{k}</span>,
            <span className="font-mono text-xs">{formatAttribute(v)}</span>,
          ])}
        />
      </Card>
    </div>
  );
}

/**
 * Render a raw attribute honestly.
 *
 * `null` and `""` mean different things in this master — a UDF nobody has filled versus a field
 * deliberately blanked — so they are shown as themselves rather than both collapsed to a dash.
 */
function formatAttribute(v: unknown): string {
  if (v === null) return 'null';
  if (v === '') return '(empty)';
  if (typeof v === 'boolean') return String(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function Fact({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded border border-slate-200 px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}
